import React, { useRef, Fragment } from 'react'
import { useSelector } from 'react-redux'

import { parseImmunizations } from '@web/helpers/parseEnhanced.js'
import { sortImmunizations } from '@web/helpers/sortBy'

import { immunization } from '@enlyt/stores/Fhir/Select'

import { useReactToPrint } from 'react-to-print'

import { useTheme } from 'contexts/ThemeProvider'
import { style as styles, themeStyles } from './styles.js'
import localization from './localization.js'

import PrintFooter from 'containers/MyHealth/components/PrintFooter'
import PrintHeader from 'containers/MyHealth/components/PrintHeader'
import Divider from 'containers/MyHealth/components/Divider'

import { Space, Layout } from 'antd'
import { PrinterFilled } from '@ant-design/icons'
import Bowser from 'bowser'

import _ from 'lodash'
import { GridRow, GridColumn, Grid, Button } from 'semantic-ui-react'
import withLocalization from '@web/helpers/localizationUtils'

const printPageStyle = `
@media print {
  @page {
    margin-top: 0;
    -webkit-margin-top: 0;
    margin-bottom: 0;
    -webkit-margin-top: 0;
  }
}
body {
  -webkit-print-color-adjust: exact;
  background:#fff !important;
  break-inside: auto !important;
}
.print{
  margin: 0 !important;
  padding: 0;
  width: auto !important;
  height: auto !important;
  z-index: 10000 !important;
}
`

const BROWSER_PAGINATION_LIMIT = {
  safari: 500,
  iOSNonSafari: 550,
  others: 750
}

const getPaginationLimitByBrowser = (isIos = false, isSafari = false) => {
  if (!isIos && !isSafari) {
    return BROWSER_PAGINATION_LIMIT.others
  }
  if (isSafari) {
    return BROWSER_PAGINATION_LIMIT.safari
  }
  return BROWSER_PAGINATION_LIMIT.iOSNonSafari
}

const getLayoutStyle = (isSafari = false, isIOS = false) => {
  if (isSafari) {
    return styles.printLayoutSafari
  }
  if (isIOS) {
    return styles.printLayoutInIos
  }
  return styles.printLayout
}

// Segment the input array, take a specific number of elements and construct a two-dimensional array.

function formatList(
  arr = [],
  maxPageHeight = 650,
  titMaxWidth = 400,
  dateMaxWidth = 80,
  fontSize = '16px'
) {
  arr = _.isArray(arr) ? arr : []

  const result = []
  let currentPage = []
  let currentPageHeight = 0

  arr.forEach(item => {
    const title = _.get(item, 'title', '')
    const content = _.get(item, 'date', '')

    const titleHeight = calculateLines(title, titMaxWidth, fontSize)
    const contentHeight = calculateLines(content, dateMaxWidth, fontSize)
    const itemHeight = Math.max(titleHeight, contentHeight) + 60 // 60 is padding top + padding bottom
    const maxCommonHeight =
      result.length === 0 ? maxPageHeight : maxPageHeight + 100

    if (
      currentPageHeight + itemHeight > maxCommonHeight &&
      currentPage.length > 0
    ) {
      result.push(currentPage)
      currentPage = []
      currentPageHeight = 0
    }

    currentPage.push(item)
    currentPageHeight += itemHeight
  })

  if (currentPage.length > 0) {
    result.push(currentPage)
  }

  return result
}

function calculateLines(text, width, fontSize) {
  const div = document.createElement('div')
  div.style.position = 'absolute'
  div.style.visibility = 'hidden'
  div.style.width = `${width}px`
  div.style.fontSize = fontSize
  div.style.whiteSpace = 'pre-wrap'
  div.style.wordWrap = 'break-word'

  document.body.appendChild(div)
  div.innerText = text

  const height = div.clientHeight
  document.body.removeChild(div)

  return height
}

const ImmunizationItem = props => {
  const { t } = props
  const isShowHeader = _.get(props, 'isShowHeader', false)

  return (
    <div className='immunization-print-item' style={styles.medicationPrintItem}>
      <Grid>
        <GridRow>
          {isShowHeader && (
            <>
              <GridColumn width={8} style={styles.header}>
                <div style={styles.vaccineName}>
                  <strong>{t('vaccine')}</strong>
                </div>
              </GridColumn>
              <GridColumn width={8} style={styles.header}>
                <div style={styles.immunizationDatesOnFile}>
                  <strong>{t('datesOnFile')}</strong>
                </div>
              </GridColumn>
            </>
          )}
        </GridRow>
      </Grid>
      <Grid>
        <GridRow style={styles.immunizationNoPaddingTop(isShowHeader)}>
          <GridColumn width={8}>
            <div style={styles.immunizationName}>
              <strong>{_.get(props, 'title')}</strong>
            </div>
          </GridColumn>
          <GridColumn width={8}>
            <div style={styles.immunizationDate}>{_.get(props, 'date')}</div>
          </GridColumn>
        </GridRow>
      </Grid>
    </div>
  )
}

const ImmunizationItemPrintLayout = props => {
  const { Header, Footer, Content } = Layout
  const showHeader = _.get(props, 'showHeader', false)
  const items = _.get(props, 'item', [])
  const totalPage = _.get(props, 'totalPage', 0)
  const curPage = _.get(props, 'curPage', 0)
  const t = _.get(props, 't', null)
  const userAgent = navigator.userAgent
  const browser = Bowser.getParser(userAgent)
  const isSafari = browser.getBrowserName() === 'Safari'
  const os = browser.getOS()
  const isIOS = os.name === 'iOS'
  const layoutStyles = getLayoutStyle(isSafari, isIOS)
  return (
    <div>
      <Layout style={layoutStyles}>
        <Header
          style={
            curPage === 1
              ? styles.printLayoutHeader
              : styles.printLayoutCommonHeader
          }
        >
          <PrintHeader title='immunization' showHeader={showHeader} />
        </Header>
        <Content style={styles.printLayoutContent}>
          {items.map((item, index) => {
            return (
              <Fragment key={index}>
                <ImmunizationItem {...item} isShowHeader={index === 0} t={t} />
                {index === items.length - 1 ? null : <Divider />}
              </Fragment>
            )
          })}
        </Content>
        <Footer style={styles.printLayoutFooter}>
          <PrintFooter
            totalPage={totalPage}
            curPage={curPage}
            id='immunization'
          />
        </Footer>
      </Layout>
    </div>
  )
}

const ImmunizationPrintContext = props => {
  const immunizations = _.get(props, 'immunizations', [])
  const t = _.get(props, 't', null)
  return (
    <div className='medication-print-container'>
      {immunizations.map((item, index) => {
        return (
          <ImmunizationItemPrintLayout
            key={index}
            item={item}
            totalPage={immunizations.length}
            curPage={index + 1}
            showHeader={index === 0}
            t={t}
          />
        )
      })}
    </div>
  )
}

const ImmunizationsPrintModal = ({ t }) => {
  const printRef = useRef(null)
  const { theme } = useTheme()
  const buttonStyles = themeStyles(theme)

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: printPageStyle,
    removeAfterPrint: false
  })

  const userAgent = navigator.userAgent
  const browser = Bowser.getParser(userAgent)
  const os = browser.getOS()
  const isIOS = os.name === 'iOS'
  const isSafari = browser.getBrowserName() === 'Safari'

  const paginationLimit = getPaginationLimitByBrowser(isIOS, isSafari)

  const items = parseImmunizations(sortImmunizations(useSelector(immunization)))
  const immunizations = formatList(items, paginationLimit, 360, 320, '14px')

  return (
    <Fragment>
      <div
        className='print'
        ref={printRef}
        style={styles.printContainer}
        id='immunization-print-area'
      >
        <ImmunizationPrintContext immunizations={immunizations} t={t} />
      </div>
      {!_.isEmpty(immunizations) && (
        <Button style={buttonStyles.downLoadButton} onClick={handlePrint}>
          <Space>
            <PrinterFilled style={styles.printerIcon} />
            <span>{t('print')}</span>
          </Space>
        </Button>
      )}
    </Fragment>
  )
}

export default withLocalization(ImmunizationsPrintModal, localization)
